import React, { useCallback, useMemo, useState } from "react"
import types from "prop-types"
import { isEmpty } from "lodash-es"
import { formSlugOptionShape, newTaskReportExportTaskReportShape } from "utils/propTypeShapes"
import { CSV_EXPORT_TYPE } from "utils/reportExportHelpers"
import TaskReportExportContext from "../TaskReportExportContext"

const TaskReportExportContextProvider = ({
  children,
  submittableForms: propSubmittableForms,
  taskReport: propTaskReport,
}) => {
  const [taskReport] = useState(propTaskReport)
  const [submittableForms] = useState(propSubmittableForms)

  const submittableFormSlugs = useMemo(() => (
    submittableForms.map(({ slug }) => slug)
  ), [submittableForms])

  const [reportExport, setReportExport] = useState({})
  const [reportExportFileType, setReportExportFileType] = useState(CSV_EXPORT_TYPE)

  const [includeOpenTasks, setIncludeOpenTasks] = useState(true)
  const [includeClosedTasks, setIncludeClosedTasks] = useState(true)

  const [earliestDueDate, setEarliestDueDate] = useState(null)
  const [latestDueDate, setLatestDueDate] = useState(null)

  const [earliestClosedDate, setEarliestClosedDate] = useState(null)
  const [latestClosedDate, setLatestClosedDate] = useState(null)

  // Only the checked forms are exported, so every form starts checked and an untouched export
  // covers the whole solution.
  const [includedFormSlugs, setIncludedFormSlugs] = useState(submittableFormSlugs)

  // The closed date range only refines closed tasks, so excluding them clears it here rather than
  // in an effect watching the checkbox.  The backend drops the dates too, but leaving them on
  // screen would suggest a filter that is not being applied.
  const toggleClosedTasks = useCallback(() => {
    setIncludeClosedTasks((currentlyIncluded) => {
      if (currentlyIncluded) {
        setEarliestClosedDate(null)
        setLatestClosedDate(null)
      }

      return !currentlyIncluded
    })
  }, [])

  const toggleOpenTasks = useCallback(() => {
    setIncludeOpenTasks((currentlyIncluded) => !currentlyIncluded)
  }, [])

  const toggleIncludedFormSlug = useCallback((slug) => {
    setIncludedFormSlugs((currentSlugs) => (
      currentSlugs.includes(slug)
        ? currentSlugs.filter((includedSlug) => includedSlug !== slug)
        : [...currentSlugs, slug]
    ))
  }, [])

  const includeAllForms = useCallback(() => setIncludedFormSlugs(submittableFormSlugs), [submittableFormSlugs])

  const excludeAllForms = useCallback(() => setIncludedFormSlugs([]), [])

  // An export must include open tasks, closed tasks, or both.  The model rejects the empty case,
  // and the form disables its action rather than letting it be submitted.
  const noTaskStatusIncluded = !includeOpenTasks && !includeClosedTasks

  // Only the checked forms are exported, so an empty selection would export nothing.
  const noSubmittableFormIncluded = isEmpty(includedFormSlugs)

  const setEarliestDueDateFilter = useCallback((date) => {
    setEarliestDueDate(isEmpty(date) ? null : date)
  }, [])

  const setLatestDueDateFilter = useCallback((date) => {
    setLatestDueDate(isEmpty(date) ? null : date)
  }, [])

  const setEarliestClosedDateFilter = useCallback((date) => {
    setEarliestClosedDate(isEmpty(date) ? null : date)
  }, [])

  const setLatestClosedDateFilter = useCallback((date) => {
    setLatestClosedDate(isEmpty(date) ? null : date)
  }, [])

  // Object exposed to context consumers
  const contextConsumerValue = {
    earliestClosedDate,
    earliestDueDate,
    excludeAllForms,
    includeAllForms,
    includeClosedTasks,
    includedFormSlugs,
    includeOpenTasks,
    latestClosedDate,
    latestDueDate,
    noSubmittableFormIncluded,
    noTaskStatusIncluded,
    reportExport,
    reportExportFileType,
    setEarliestClosedDate: setEarliestClosedDateFilter,
    setEarliestDueDate: setEarliestDueDateFilter,
    setLatestClosedDate: setLatestClosedDateFilter,
    setLatestDueDate: setLatestDueDateFilter,
    setReportExport,
    setReportExportFileType,
    submittableForms,
    taskReport,
    toggleClosedTasks,
    toggleIncludedFormSlug,
    toggleOpenTasks,
  }

  return (
    <TaskReportExportContext.Provider value={contextConsumerValue}>
      {children}
    </TaskReportExportContext.Provider>
  )
}

TaskReportExportContextProvider.propTypes = {
  children: types.node.isRequired,
  submittableForms: types.arrayOf(formSlugOptionShape).isRequired,
  taskReport: newTaskReportExportTaskReportShape.isRequired,
}

export default TaskReportExportContextProvider
